// WhatsApp service for checking WhatsApp status and connection

import apiService, { ApiError } from './apiService';

interface WhatsAppStatusResponse {
  /** Backend states: 'connected' | 'pending' | 'failed' | 'not_configured'. */
  status: string;
  whatsapp_number?: string;
}

interface WhatsAppDisconnectResponse {
  success: boolean;
  waba_status: string;
  deregistered: boolean;
  deregister_error?: unknown;
}

/** What Meta's Embedded Signup hands back through window.postMessage. */
export interface MetaSignupData {
  business_id: string;
  phone_number_id: string;
  waba_id: string;
  event?: string;
}

export interface AgentWhatsAppResponse {
  success: boolean;
  message: string;
  /** E.164 number Meta reported for the connection that was just changed. */
  whatsapp_number?: string | null;
}

/** One line for the whole company, or one per location. */
export type WhatsAppMode = 'central' | 'per_site';

/** Connection of the logged-in salon's primary WhatsApp line (``GET /whatsapp/connectivity``). */
export interface WhatsAppConnectivity {
  is_connected: boolean;
  whatsapp_number?: string | null;
  waba_status?: string | null;
  whatsapp_profile?: Record<string, unknown> | null;
  message?: string;
}

/** Body of the onboarding's Embedded Signup completion — for the LOGGED-IN salon. */
export interface CompleteOnboardingRequest {
  auth_code: string;
  signup_data: MetaSignupData;
  whatsapp_use_reserved_number?: boolean;
  /**
   * DEPRECATED — one release only. The backend before 01.10.2026 picked the
   * salon by this e-mail (without a login); it is still sent so this web app
   * also works against that backend while the two are deployed one after the
   * other. The current backend only checks it against the login (403 on a
   * mismatch). Remove it together with the backend field.
   */
  business_email?: string;
}

export interface CompleteOnboardingResponse {
  success: boolean;
  message: string;
  facebook_permissions_url?: string | null;
  detail?: string;
}

const CONNECT_FALLBACK = 'WhatsApp konnte nicht verbunden werden.';

/**
 * What the salon reads when connecting a WhatsApp number failed.
 *
 * A 409 means the number belongs to another salon; the backend's German
 * sentence ("Diese WhatsApp-Nummer ist bereits mit dem Salon „…“ verbunden.
 * Bitte trenne sie dort zuerst.") says what to do and is shown verbatim.
 * Everything else keeps the "Fehler: …" prefix.
 */
export const whatsappConnectErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError && error.status === 409 && error.message) return error.message;
  const message = error instanceof Error ? error.message : '';
  return `Fehler: ${message || CONNECT_FALLBACK}`;
};

const signupBody = (signupData: MetaSignupData) => ({
  business_id: String(signupData.business_id),
  phone_number_id: String(signupData.phone_number_id),
  waba_id: String(signupData.waba_id),
  event: String(signupData.event || 'FINISH'),
  type: 'WA_EMBEDDED_SIGNUP',
  version: '3',
});

const whatsappService = {
  /**
   * WhatsApp (WABA) connection status of the tenant.
   *
   * Errors are NOT swallowed: a failing request used to be turned into
   * "disconnected", which made a broken endpoint look like a broken WhatsApp
   * connection. Callers decide what an unknown status should look like.
   */
  getStatus: async (): Promise<WhatsAppStatusResponse> => {
    return await apiService.get<WhatsAppStatusResponse>('/whatsapp/status');
  },

  // Unlink the connected WhatsApp (WABA) number so it can be re-linked.
  // Deregisters the number at Meta (best-effort) and resets our DB to 'pending'.
  disconnect: async (): Promise<WhatsAppDisconnectResponse> => {
    return await apiService.post<WhatsAppDisconnectResponse>('/whatsapp/disconnect', {});
  },

  /**
   * Attach a FURTHER WhatsApp account to this tenant (one per location).
   * The account's primary connection stays where it is — this one is stored
   * against the agent number it belongs to.
   */
  connectAdditionalNumber: async (
    authCode: string,
    signupData: MetaSignupData,
  ): Promise<AgentWhatsAppResponse> => {
    return await apiService.post<AgentWhatsAppResponse>(
      '/whatsapp/connect-additional-number',
      {
        auth_code: String(authCode),
        signup_data: signupBody(signupData),
      },
    );
  },

  /**
   * Connection state of the logged-in salon's primary WhatsApp line — the
   * onboarding's status poll. Read with the login token, never by e-mail (the
   * e-mail-keyed ``/status-public`` showed any salon's connection to anybody).
   * Errors reject; callers decide what an unknown state looks like.
   */
  getConnectivity: async (): Promise<WhatsAppConnectivity> => {
    return await apiService.get<WhatsAppConnectivity>('/whatsapp/connectivity', { cache: 'no-store' });
  },

  /**
   * Finish the onboarding's Embedded Signup for the LOGGED-IN salon (the
   * login token goes along; the path keeps its old name). Failures reject
   * with an ``ApiError`` carrying the backend's ``detail`` and status; show
   * them with ``whatsappConnectErrorMessage``.
   */
  completeOnboarding: async (
    request: CompleteOnboardingRequest,
  ): Promise<CompleteOnboardingResponse> => {
    const body: Record<string, unknown> = {
      auth_code: String(request.auth_code),
      signup_data: signupBody(request.signup_data),
    };
    if (request.business_email && request.business_email.trim()) {
      body.business_email = request.business_email;
    }
    if (request.whatsapp_use_reserved_number !== undefined) {
      body.whatsapp_use_reserved_number = request.whatsapp_use_reserved_number;
    }
    return await apiService.post<CompleteOnboardingResponse>('/whatsapp/complete-onboarding-public', body);
  },

  /** Detach the WhatsApp connection from one agent number (salons stay). */
  disconnectAgentNumber: async (phoneNumber: string): Promise<AgentWhatsAppResponse> => {
    return await apiService.delete<AgentWhatsAppResponse>(
      `/whatsapp/agent-number/${encodeURIComponent(phoneNumber)}`,
    );
  },

  /** Remember whether the tenant runs one WhatsApp line or one per location. */
  setMode: async (mode: WhatsAppMode): Promise<AgentWhatsAppResponse> => {
    return await apiService.put<AgentWhatsAppResponse>('/whatsapp/mode', { mode });
  },
};

export default whatsappService; 